import { useState } from "react";
import {
  Alert,
  Badge,
  Checkbox,
  FormModal,
  Input,
} from "@marekl/adminer/components";
import {
  useResetUserPassword,
  useUpdateUser,
  useCreateUser,
  type Assignment,
  type UpdateUserPayload,
  type User,
} from "../../../auth/queries";
import { notifySuccess } from "@marekl/adminer/api";
import { ROLE_LABEL, SCOPE_LABEL } from "../../../lib/i18n/roles";
import styles from "./UsersPage.module.css";

function assignmentLabel(a: Assignment): string {
  const role = ROLE_LABEL[a.role] ?? a.role;
  if (a.scope_type === "global") return role;
  if (a.scope_name) return `${role} · ${a.scope_name}`;
  return `${role} (${SCOPE_LABEL[a.scope_type]})`;
}

export function AssignmentChips({ assignments }: { assignments: Assignment[] }) {
  if (assignments.length === 0) {
    return <Badge variant="muted">Žádné</Badge>;
  }
  return (
    <div className={styles.roleChips}>
      {assignments.map((a, i) => (
        <Badge
          key={i}
          variant={a.virtual ? "muted" : "info"}
        >
          {assignmentLabel(a)}
        </Badge>
      ))}
    </div>
  );
}

export function EditUserModal({ user, onClose }: { user: User; onClose: () => void }) {
  const updateUser = useUpdateUser(user.id);
  const [form, setForm] = useState<Required<UpdateUserPayload>>({
    display_name: user.display_name ?? "",
    email: user.email ?? "",
    active: user.active,
    password_locked: user.password_locked,
  });

  async function submit() {
    await updateUser.mutateAsync(form);
    notifySuccess("Uživatel byl upraven.");
    onClose();
  }

  return (
    <FormModal
      open
      title={`Upravit: ${user.email}`}
      submitLabel="Uložit"
      busy={updateUser.isPending}
      error={updateUser.error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Input
        id="edit-display-name"
        label="Jméno"
        value={form.display_name}
        onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
      />
      <Input
        id="edit-email"
        label="E-mail"
        type="email"
        required
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
      />
      <Checkbox
        label="Účet je aktivní"
        checked={form.active}
        onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
      />
      <Checkbox
        label="Zablokovat změnu hesla"
        checked={form.password_locked}
        onChange={(e) => setForm((f) => ({ ...f, password_locked: e.target.checked }))}
      />
      <Alert variant="info">
      Role uživatele se spravují samostatně; přiřazení nelze měnit z tohoto dialogu.
      </Alert>
    </FormModal>
  );
}

export function ResetPasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
  const resetPw = useResetUserPassword(user.id);
  // Present ONLY when the server has no mail configured; then the admin has
  // to relay the link. With mail working nothing secret appears here.
  const [fallbackURL, setFallbackURL] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    const res = await resetPw.mutateAsync();
    setFallbackURL(res.set_password_url ?? null);
    setDone(true);
    if (!res.set_password_url) {
      notifySuccess("Odkaz pro nastavení hesla jsme odeslali.");
      onClose();
    }
  }

  return (
    <FormModal
      open
      title={`Obnovit heslo: ${user.email}`}
      submitLabel={done ? "Zavřít" : "Obnovit heslo"}
      busy={resetPw.isPending}
      error={resetPw.error}
      onSubmit={done ? onClose : submit}
      onClose={onClose}
    >
      {done && fallbackURL ? (
        <Alert variant="warn" title="Odesílání e-mailů není nastavené">
          Předejte uživateli tenhle odkaz; nastaví si jím heslo. Zobrazuje se jen teď.
          <br />
          <code style={{ wordBreak: "break-all" }}>{fallbackURL}</code>
        </Alert>
      ) : (
        <Alert variant="info">
          Heslo nezadáváte. Dosavadní heslo přestane platit, uživatel dostane e-mailem odkaz
          pro nastavení nového a bude odhlášen na všech zařízeních.
        </Alert>
      )}
    </FormModal>
  );
}

export function CreateUserModal({ onClose }: { onClose: () => void }) {
  const createUser = useCreateUser();
  const [form, setForm] = useState({
    email: "",
    display_name: "",
    active: true,
    password_locked: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit() {
    const local: Record<string, string> = {};
    if (!form.email.trim()) local.email = "E-mail je povinný";
    if (!form.display_name.trim()) local.display_name = "Jméno je povinné";
    if (Object.keys(local).length > 0) {
      setErrors(local);
      return;
    }
    await createUser.mutateAsync(form);
    notifySuccess("Uživatel byl vytvořen.");
    onClose();
  }

  return (
    <FormModal
      open
      title="Vytvořit uživatele"
      submitLabel="Vytvořit"
      busy={createUser.isPending}
      error={createUser.error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Input
        id="create-email"
        label="E-mail"
        type="email"
        required
        value={form.email}
        onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setErrors((x) => ({ ...x, email: "" })); }}
        error={errors.email}
        autoComplete="off"
      />
      <Input
        id="create-name"
        label="Jméno"
        required
        value={form.display_name}
        onChange={(e) => { setForm((f) => ({ ...f, display_name: e.target.value })); setErrors((x) => ({ ...x, display_name: "" })); }}
        error={errors.display_name}
        autoComplete="off"
      />
      <Checkbox
        label="Účet je aktivní"
        checked={form.active}
        onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
      />
      <Checkbox
        label="Zablokovat změnu hesla"
        checked={form.password_locked}
        onChange={(e) => setForm((f) => ({ ...f, password_locked: e.target.checked }))}
      />
      <Alert variant="info">
      Po vytvoření můžete uživateli přiřadit role.
      </Alert>
    </FormModal>
  );
}
